import { useMemo } from 'react';
import { VStack } from '@components/base/layout';
import { useAdminFormPage } from '../../hooks/useAdminFormPage';
import { AdminFormPage } from '../../components/AdminFormPage';
import { getModuleFormConfig } from '../../configs/moduleFormConfig';
import { modulesService } from '../../api/modulesService';
import { parseJsonField } from '../../utils/parseJsonField';
import type { CreateModulePayload, UpdateModulePayload } from '../../api/modulesService';
import { ModuleRulesSection } from './ModuleRulesSection';

interface ModuleParams {
  slug?: string;
}

export function ModuleFormPage() {
  const formPage = useAdminFormPage<unknown, ModuleParams>({
    entityName: 'module',
    getConfig: (mode, params) => getModuleFormConfig(mode, params.slug),
    getEditId: (params) => params.slug,
    queryKey: (params) => ['admin', 'modules', params.slug],
    fetchFn: (params) => {
      if (!params.slug) {
        return Promise.reject(new Error('Module slug is required'));
      }
      return modulesService.get(params.slug);
    },
    mapToInitialValues: (entity) => {
      const mod = entity as Record<string, unknown>;
      return {
        slug: mod.slug,
        name: mod.name,
        description: (mod.description as string) ?? '',
        version: (mod.version as string) ?? '',
        config: mod.config ? JSON.stringify(mod.config, null, 2) : '',
        is_active: mod.is_active,
      };
    },
    mutationFn: async (payload, isEdit, params) => {
      const configValue = parseJsonField(payload.config);
      const cleanedPayload = {
        ...payload,
        config: configValue,
        description: (payload.description as string) || null,
        version: (payload.version as string) || null,
      };
      if (isEdit && params.slug) {
        return modulesService.update(params.slug, cleanedPayload as UpdateModulePayload);
      }
      return modulesService.create(cleanedPayload as CreateModulePayload);
    },
    invalidateKey: () => ['admin', 'modules'],
    navigateBackPath: () => '/admin/modules',
  });

  const breadcrumbs = useMemo(() => [
    { label: 'Modules', href: '/admin/modules' },
    { label: formPage.isEdit ? 'Edit Module' : 'Create Module' },
  ], [formPage.isEdit]);

  const errorBreadcrumbs = useMemo(() => [
    { label: 'Modules', href: '/admin/modules' },
    { label: 'Edit' },
  ], []);

  // Guard which surface to render the module-rules section on. Only
  // shown in edit mode and only once the route has a slug. It mounts
  // *outside* the fetch loading / error short-circuits owned by
  // `AdminFormPage`, so the section never appears when the module
  // itself failed to load (AdminFormPage renders a different JSX tree
  // in those states and we early-return from this component before
  // reaching the section).
  const showRulesSection = formPage.isEdit && formPage.params.slug !== undefined;

  return (
    <VStack gap={6}>
      <AdminFormPage
        formPage={formPage}
        entityLabel="Module"
        breadcrumbs={breadcrumbs}
        errorBreadcrumbs={errorBreadcrumbs}
        genericFetchErrorMessage="An unexpected error occurred."
      />
      {showRulesSection && !formPage.isFetchLoading && formPage.fetchError === null && (
        <ModuleRulesSection moduleSlug={formPage.params.slug ?? ''} />
      )}
    </VStack>
  );
}
